import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import api, { apiErr, isPaywall } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PaywallNotice } from "@/components/shared";
import { toast } from "sonner";
import ReplyResult from "@/components/ReplyResult";
import { useAuth } from "@/context/AuthContext";
import ChildHome from "@/pages/homes/ChildHome";
import TeenHome from "@/pages/homes/TeenHome";
import ParentHome from "@/pages/homes/ParentHome";
import {
  Sparkles, Search, Loader2, ArrowRight, MessageSquare,
  Scale, BookOpen, Clapperboard, Users, BrainCircuit, ArrowUpRight,
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1741356474357-72188f76e801?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsYWNrJTIwZ29sZCUyMGx1eHVyeXxlbnwwfHx8fDE3ODYyMTg4Mjl8MA&ixlib=rb-4.1.0&q=85";

// Bloc 2 — exactement 6 cartes, toutes fonctionnelles.
const ACTIONS = [
  { id: "repondre", to: "/reply", icon: MessageSquare },
  { id: "analyser", to: "/analyze", icon: Search },
  { id: "droits", to: "/rights", icon: Scale },
  { id: "bibliotheque", to: "/bibliotheque", icon: BookOpen },
  { id: "creer", to: "/create", icon: Clapperboard },
  { id: "communaute", to: "/community", icon: Users },
];

// Bloc 4 — signature de la plateforme (préparation, non encore développé).
const SOCIAL_TEASER = ["profiles", "posts", "comments", "follows", "groups", "messaging", "notifications"];

function CountUp({ value }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf; const start = performance.now(); const dur = 1200;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{n.toLocaleString("fr-FR")}</>;
}

export default function Home() {
  const { user } = useAuth();
  const p = user?.profile;
  if (p === "child") return <ChildHome />;
  if (p === "teen") return <TeenHome />;
  if (p === "parent") return <ParentHome />;
  return <AdultHome />;
}

function AdultHome() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const DEMO_COMMENT = t("home.demoComment");
  const DEMO_REPLY = t("home.demoReply");
  const [text, setText] = useState("");
  const [gen, setGen] = useState(false);
  const [result, setResult] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [stats, setStats] = useState(null);
  const inputRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 400);
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
    return () => clearTimeout(t);
  }, []);

  const reply = async (override) => {
    const c = (typeof override === "string" ? override : text).trim();
    if (!c) { inputRef.current?.focus(); toast.info("Collez d'abord un message."); return; }
    if (typeof override === "string") setText(c);
    heroRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setGen(true); setPaywall(false); setResult(null);
    try {
      const { data } = await api.post("/ai/reply", { comment: c });
      setResult(data);
    } catch (e) {
      if (isPaywall(e)) setPaywall(true);
      else toast.error(apiErr(e));
    } finally { setGen(false); }
  };

  const analyzeNow = () => {
    const c = text.trim();
    navigate(c ? `/analyze?situation=${encodeURIComponent(c)}&auto=1` : "/analyze");
  };

  const reset = () => { setResult(null); setPaywall(false); setText(""); inputRef.current?.focus(); };

  const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } } };
  const rise = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
  const press = { whileHover: { y: -4 }, whileTap: { scale: 0.96 }, transition: { type: "spring", stiffness: 400, damping: 22 } };

  return (
    <Container className="max-w-6xl">
      {/* ================= BLOC 1 — HERO (au-dessus de la ligne de flottaison) ================= */}
      <section ref={heroRef} className="relative overflow-hidden rounded-[2rem] border border-white/10 noise mb-14" data-testid="home-hero">
        <img src={HERO_BG} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-[0.20] mix-blend-screen pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/55 via-[#050505]/85 to-[#050505] pointer-events-none" />
        <div className="absolute -top-32 -right-24 w-[34rem] h-[34rem] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.24), transparent 65%)" }} />

        <motion.div variants={stagger} initial="hidden" animate="show" className="relative px-5 py-12 md:px-16 md:py-16 flex flex-col justify-center min-h-[82vh]">
          {/* Logo */}
          <motion.div variants={rise} className="flex items-center gap-2.5 mb-8 self-start">
            <div className="w-10 h-10 rounded-xl gold-gradient flex items-center justify-center">
              <span className="text-black font-bold font-display text-lg">B</span>
            </div>
            <span className="font-display text-2xl tracking-tight">BIZARRE <span className="gold-text">AI</span></span>
          </motion.div>

          {/* Titre + sous-titre */}
          <motion.h1 variants={rise} data-testid="home-hero-title"
            className="font-display text-5xl md:text-7xl lg:text-[5.5rem] tracking-tight leading-[0.98] font-light max-w-4xl">
            {t("home.heroTitle1")}<br /><span className="gold-text">{t("home.heroTitle2")}</span>
          </motion.h1>
          <motion.p variants={rise} className="text-white/65 text-lg md:text-2xl mt-6 max-w-2xl font-light leading-snug">
            {t("home.heroSub1")}<br className="hidden md:block" />
            <span className="text-white/90"> {t("home.heroSub2")}</span>
          </motion.p>

          {/* Grand champ + boutons */}
          <motion.div variants={rise} className="mt-7 md:mt-8 max-w-3xl w-full">
            <div className="rounded-3xl border border-gold/30 bg-white/[0.04] backdrop-blur-xl p-4 md:p-5 shadow-[0_0_70px_-15px_rgba(212,175,55,0.45)]">
              <textarea ref={inputRef} data-testid="hero-analyze-input" rows={5} value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") reply(); }}
                placeholder={t("home.placeholder")}
                className="w-full bg-transparent outline-none resize-none text-white text-lg md:text-xl placeholder:text-white/40 min-h-[130px]" />
              <div className="flex justify-end mt-2">
                <MicButton testid="hero-analyze-mic" value={text} onChange={setText} />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-3">
                <motion.button whileTap={{ scale: 0.97 }} data-testid="hero-reply-button" onClick={() => reply()} disabled={gen}
                  className="flex-1 inline-flex items-center justify-center gap-2 gold-gradient text-black rounded-full px-6 py-4 md:py-5 text-base md:text-lg font-bold tracking-wide hover:opacity-90 transition-opacity shadow-[0_0_30px_-8px_rgba(212,175,55,0.7)] disabled:opacity-60">
                  {gen ? <Loader2 size={20} className="animate-spin" /> : <Sparkles size={20} />} {gen ? t("common.loading") : t("home.replyNow")}
                </motion.button>
                <motion.button whileTap={{ scale: 0.97 }} data-testid="hero-analyze-button" onClick={analyzeNow}
                  className="inline-flex items-center justify-center gap-2 border border-gold/40 text-gold rounded-full px-6 py-4 md:py-5 text-base font-semibold hover:bg-gold/10 transition-colors">
                  <Search size={18} /> {t("home.analyzeNow")}
                </motion.button>
              </div>
            </div>
            {!result && !paywall && !gen && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-4 text-white/45 text-xs md:text-sm" data-testid="home-steps">
                <span><span className="text-gold font-semibold">1.</span> {t("home.step1")}</span>
                <ArrowRight size={13} className="text-gold/50" />
                <span><span className="text-gold font-semibold">2.</span> {t("home.step2")}</span>
                <ArrowRight size={13} className="text-gold/50" />
                <span><span className="text-gold font-semibold">3.</span> {t("home.step3")}</span>
              </div>
            )}
          </motion.div>

          {/* Indicateur de progression pendant la génération */}
          {gen && !result && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 max-w-3xl w-full flex items-center gap-3 rounded-2xl border border-gold/20 bg-white/[0.03] p-4" data-testid="home-loading">
              <Loader2 size={20} className="text-gold animate-spin shrink-0" />
              <div>
                <p className="text-white/85 text-sm font-medium">{t("home.writing")}</p>
                <p className="text-white/40 text-xs">{t("home.writingSub")}</p>
              </div>
            </motion.div>
          )}

          {/* Résultat inline */}
          {(result || paywall) && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 max-w-3xl w-full" data-testid="home-results">
              {paywall ? <PaywallNotice /> : <ReplyResult data={result} idPrefix="home" />}
              <button data-testid="home-reset" onClick={reset} className="mt-3 text-white/40 text-xs hover:text-white transition-colors">
                {t("home.resetAnother")}
              </button>
            </motion.div>
          )}
        </motion.div>
      </section>

      {/* ================= BLOC CONFIANCE — CHIFFRES ================= */}
      <section className="mb-14 -mt-4" data-testid="home-stats">
        <p className="text-center text-white/40 text-[11px] uppercase tracking-[0.25em] mb-5">{t("home.statsTitle")}</p>
        <div className="grid grid-cols-3 gap-3 md:gap-6 max-w-3xl mx-auto">
          {[
            { key: "replies", label: t("home.statReplies") },
            { key: "analyses", label: t("home.statAnalyses") },
            { key: "users", label: t("home.statUsers") },
          ].map((s) => (
            <div key={s.key} className="rounded-2xl border border-white/10 bg-white/[0.02] noise py-5 px-2 text-center" data-testid={`home-stat-${s.key}`}>
              <p className="font-display text-2xl md:text-4xl gold-text leading-none">
                {stats ? <CountUp value={stats[s.key]} /> : "…"}<span className="text-gold/60">+</span>
              </p>
              <p className="text-white/50 text-[10px] md:text-xs mt-2 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= BLOC 2 — QUE VOULEZ-VOUS FAIRE ? ================= */}
      <section className="mb-16" data-testid="home-actions">
        <h2 className="font-display text-2xl md:text-4xl tracking-tight text-white/90 mb-7">{t("home.whatToDo")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {ACTIONS.map((a) => (
            <motion.button key={a.id} data-testid={`home-action-${a.id}`} onClick={() => navigate(a.to)} {...press}
              className="group relative overflow-hidden card-surface noise p-5 md:p-6 flex flex-col text-left hover:border-gold/50 transition-colors min-h-[150px]">
              <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.18), transparent 70%)" }} />
              <div className="w-11 h-11 rounded-xl bg-gold/10 border border-gold/25 flex items-center justify-center mb-3 group-hover:bg-gold/20 transition-colors">
                <a.icon size={20} className="text-gold" strokeWidth={1.6} />
              </div>
              <p className="font-display text-lg md:text-xl gold-text leading-tight">{t(`home.actions.${a.id}.title`)}</p>
              <p className="text-white/50 text-xs md:text-sm mt-1.5 leading-snug flex-1">{t(`home.actions.${a.id}.desc`)}</p>
              <span className="inline-flex items-center gap-1 text-gold text-xs font-medium mt-3 group-hover:gap-2 transition-all">{t("common.open")} <ArrowRight size={13} /></span>
            </motion.button>
          ))}
        </div>
      </section>

      {/* ================= BLOC 3 — EXEMPLE RÉEL ================= */}
      <section className="mb-16" data-testid="home-example">
        <h2 className="font-display text-2xl md:text-4xl tracking-tight text-white/90 mb-7">{t("home.exampleTitle")}</h2>
        <div className="rounded-3xl border border-gold/20 bg-white/[0.02] noise p-6 md:p-8 max-w-3xl">
          <p className="text-white/40 text-[11px] uppercase tracking-[0.2em] mb-3">{t("home.exampleComment")}</p>
          <p className="text-white/85 text-lg md:text-xl font-display italic">« {DEMO_COMMENT} »</p>
          <div className="flex items-center gap-2 text-gold my-4">
            <div className="h-px flex-1 bg-gradient-to-r from-gold/40 to-transparent" />
            <BrainCircuit size={18} />
            <span className="text-xs uppercase tracking-[0.2em]">{t("home.exampleReplyLabel")}</span>
            <div className="h-px flex-1 bg-gradient-to-l from-gold/40 to-transparent" />
          </div>
          <p className="text-white/90 text-base md:text-lg leading-relaxed border-l-2 border-gold/50 pl-4">{DEMO_REPLY}</p>
          <motion.button whileTap={{ scale: 0.97 }} data-testid="home-example-try" onClick={() => reply(DEMO_COMMENT)} disabled={gen}
            className="mt-6 inline-flex items-center gap-2 gold-gradient text-black rounded-full px-6 py-3 text-sm md:text-base font-bold hover:opacity-90 transition-opacity disabled:opacity-60">
            {gen ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />} {t("home.tryExample")}
          </motion.button>
        </div>
      </section>

      {/* ================= BLOC 4 — COMMUNAUTÉ (préparation, signature) ================= */}
      <section className="mb-10" data-testid="home-community">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 noise p-6 md:p-10">
          <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.12), transparent 70%)" }} />
          <div className="relative">
            <p className="text-gold text-[11px] uppercase tracking-[0.25em] mb-3">{t("home.soon")}</p>
            <h2 className="font-display text-2xl md:text-4xl tracking-tight text-white/90 max-w-2xl leading-tight">
              {t("home.communityTitle1")} <span className="gold-text">{t("home.communityTitle2")}</span>.
            </h2>
            <p className="text-white/55 text-sm md:text-base mt-4 max-w-2xl leading-relaxed">
              {t("home.communityDesc")}
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {SOCIAL_TEASER.map((s) => (
                <span key={s} className="rounded-full border border-white/12 bg-white/[0.03] px-3 py-1.5 text-xs text-white/60">{t(`home.teaser.${s}`)}</span>
              ))}
            </div>
            <motion.button whileTap={{ scale: 0.97 }} data-testid="home-community-cta" onClick={() => navigate("/community")}
              className="mt-7 inline-flex items-center gap-2 border border-gold/40 text-gold rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-gold/10 transition-colors">
              {t("home.discoverCommunity")} <ArrowUpRight size={16} />
            </motion.button>
          </div>
        </div>
      </section>

      {/* Signature */}
      <div className="mt-4 pt-8 border-t border-white/10 text-center">
        <p className="font-display text-lg md:text-2xl font-light text-white/80">
          {t("home.signature")} <span className="gold-text">{t("home.signatureGold")}</span>
        </p>
        <p className="text-white/30 text-xs mt-2">{t("home.signatureSub")}</p>
      </div>
    </Container>
  );
}
